import { Progress, useHelmet } from '@agro-club/agroclub-shared'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { Elevator } from 'modules/domain/warehouses/types'
import { endpoints } from 'modules/endpoints'
import { getPersonalNumberLabel } from 'modules/utils/numbers/formatPersonalNumber'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { CompanyLink } from 'views/components/CompanyLink/CompanyLink'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import * as Layout from 'views/layouts/NewLayout'
import * as Header from 'views/ui/Header/Header'
import ElevatorRoutes from './routes'
import { AButton } from 'views/components/Analytics'
import { EditElevatorModal } from './EditElevatorModal'
import { Card } from 'views/components/Card/Card'
import { KeyValue } from 'views/components/KeyValue/KeyValue'
import { DASH } from 'modules/constants'
import { Costs } from './Costs'
import { CreatedUpdatedTag } from 'views/components/Tags/CreatedUpdatedTag'
import { labelQuantity } from 'modules/utils/numbers/formatQuantity'
import { YesNoUnknown } from 'modules/domain/types'
export const ElevatorDetail: FC = () => {
  const { id } = useParams()
  const { t } = useTranslation('warehouses')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [progress, elevator, refetch] = useSingleEntity<Elevator>(endpoints.elevators(id))
  useHelmet({ title: `${t('elevator')} - ${elevator?.title}` })
  if (!elevator) return null
  if (progress === Progress.WORK) return <ItemLoadingLayout id={id} />

  const warehouseYNU = (value: YesNoUnknown | undefined) => {
    if (!value) return DASH
    return t(`warehouse:yesNoUnknown.${value}`)
  }

  return (
    <APageWrapper page="elevatorDetail" place="elevatorDetail">
      <Layout.WrapperContent>
        <Layout.Header>
          <Layout.TopHeader>
            <Header.Breadcrumbs
              routes={[
                {
                  path: ElevatorRoutes.List,
                  breadcrumbName: t('elevators'),
                },
                {
                  breadcrumbName: elevator.title,
                },
              ]}
            />
            <AButton
              id="a"
              color="secondary"
              onClick={() => setIsEditModalOpen(true)}
              size="medium"
              progress={progress}
            >
              {t('common:edit')}
            </AButton>
          </Layout.TopHeader>
          <Layout.PageTitleWrapper>
            <Header.Title margin="8px" compact title={elevator.title}>
              <CreatedUpdatedTag instance={elevator} />
            </Header.Title>
          </Layout.PageTitleWrapper>
        </Layout.Header>
        <Layout.ContentTwoColumns>
          <Card.Container>
            <Card.GapableContent>
              <Card.Title>{t('generalInfo')}</Card.Title>
              <Card.Container bordered>
                <Card.GapableContent>
                  <KeyValue label={t('address')}>{elevator.address.address}</KeyValue>
                  <KeyValue label={t('common:company')}>
                    <CompanyLink company={elevator.company} />{' '}
                    {elevator.company.inn && `/ ${getPersonalNumberLabel()}: ${elevator.company.inn}`}
                  </KeyValue>
                  <KeyValue label={t('railwayStation')}>
                    {elevator.railway_station
                      ? `${elevator.railway_station.title} (${elevator.railway_station.station_code})`
                      : DASH}
                  </KeyValue>
                  <KeyValue label={t('services')}>
                    {elevator.provided_services ? elevator.provided_services?.split(',').join(', ') : DASH}
                  </KeyValue>
                  <Card.Grid cols={2} columnGap={32}>
                    <KeyValue label={labelQuantity(t('capacity'))}>
                      {elevator.capacity ? elevator.capacity : DASH}
                    </KeyValue>
                    <KeyValue label={t('rail_car_capacity')}>
                      {elevator.rail_car_capacity ? elevator.rail_car_capacity : DASH}
                    </KeyValue>
                    <KeyValue label={t('car_delivery')}>{warehouseYNU(elevator.address.car_delivery)}</KeyValue>
                    <KeyValue label={t('railway_delivery')}>{warehouseYNU(elevator.address.railway_delivery)}</KeyValue>
                  </Card.Grid>
                </Card.GapableContent>
              </Card.Container>
              <Costs elevator={elevator} />
            </Card.GapableContent>
          </Card.Container>
          <div></div>
        </Layout.ContentTwoColumns>
      </Layout.WrapperContent>
      {isEditModalOpen && (
        <EditElevatorModal onClose={() => setIsEditModalOpen(false)} onSuccess={refetch} elevator={elevator} />
      )}
    </APageWrapper>
  )
}
